import type { TranslationKeys } from "./vi";

const en: TranslationKeys = {
    // ====== Common ======
    common: {
        getStarted: "Get Started",
        login: "Login",
        signInWithGoogle: "Sign in with Google",
        back: "Back",
        viewAll: "View all",
        explore: "Explore",
        upload: "Upload image",
        viewSamples: "View samples",
        textPrompt: "Text prompt",
        takePhoto: "Take photo",
        generateFromText: "Generate from text",
        systemOnline: "System online",
        aiTools: "AI tools",
        industries: "industries",
        termsOfService: "Terms of Service",
        bySigningIn: "By signing in, you agree to our terms of service.",
        copyright: "© 2025 MindAI · AI for every industry",
        imagesPerDay: "images/day",
        loading: "Loading...",
        signOut: "Sign out",
        goToDashboard: "Go to Dashboard",
        error: "Error",
    },

    // ====== Home Page ======
    home: {
        badge: "Leading Industry AI Platform",
        heroTitle1: "Empower Your",
        heroTitle2: "Industry with AI",
        heroDescription: "Specialized AI toolkit for Architecture, Spa & Beauty, Photo Editing. Each industry — an optimized solution.",
        servicesByIndustry: "Services by Industry",
        stats: {
            aiTools: "AI Tools",
            industriesCount: "Industries",
            processing: "Processing Time",
        },
        features: {
            fast: { title: "Ultra Fast", desc: "Processing <5s" },
            secure: { title: "Secure", desc: "E2E Encryption" },
            multiPlatform: { title: "Cross-platform", desc: "Web & Mobile" },
        },
        tabs: {
            services: "Services",
            pricing: "Pricing",
            cases: "Cases",
            profile: "Profile",
        },
        pricing: {
            title: "Pricing",
            subtitle: "Choose the plan that fits your needs",
            free: "Free",
            popular: "Popular",
            perDay: "images/day",
            perMonth: "images/month",
            threads: "threads",
            currentPlan: "Current plan",
            upgrade: "Upgrade",
            startFree: "Start for free",
            features: {
                dailyLimit: "Daily limit",
                concurrent: "Concurrent processing",
                monthlyEst: "Monthly estimate",
                allTools: "All AI tools",
                support: "Support",
                priority: "Priority",
                basic: "Basic",
            },
        },
    },

    // ====== Login ======
    login: {
        welcomeBack: "Welcome back",
        subtitle: "Sign in to access MindAI",
    },

    // ====== Dashboard ======
    dashboard: {
        greeting: {
            morning: "Good morning",
            afternoon: "Good afternoon",
            evening: "Good evening",
        },
        chooseIndustry: "choose an industry",
        statusBar: "System online · 12 AI tools · 3 industries",
    },

    // ====== Sidebar ======
    sidebar: {
        home: "Home",
        architecture: "Arch",
        spa: "Spa",
        photo: "Photo",
    },

    // ====== Industries ======
    industries: {
        architecture: {
            name: "Architecture AI",
            nameShort: "Architecture",
            nameEn: "Architecture",
            description: "AI blueprints & 3D renders",
            descriptionLong: "Design, render and transform architectural spaces with AI.",
            tools: "4 tools",
            statusBar: "Advanced AI · Real-time rendering",
        },
        spa: {
            name: "Spa & Beauty AI",
            nameShort: "Spa & Beauty",
            nameEn: "SPA & Wellness",
            description: "Personalized skincare",
            descriptionLong: "Skin analysis, beauty consultation, virtual makeup and hair styling with AI.",
            tools: "4 tools",
            statusBar: "AI reference · Secure · Photos deleted after 24h",
        },
        photoEdit: {
            name: "Photo Editing AI",
            nameShort: "Photo Editing",
            nameEn: "Photo Editing",
            description: "Professional editing in 1 click",
            descriptionLong: "Remove background, upscale, color correct and create art with AI.",
            tools: "4 tools",
            statusBar: "JPG, PNG, WebP · Max 20MB · Secure",
        },
    },

    // ====== Architecture Tools ======
    archTools: {
        interior: { name: "Interior Design AI", desc: "Upload a room photo — AI redesigns in styles: modern, minimal, Scandinavian, Japanese.", features: ["Room redesign", "Style swap", "Furniture", "3D Render"] },
        exterior: { name: "Exterior Rendering", desc: "Create facade, garden, and building renders from sketches or text descriptions.", features: ["Facade", "Garden", "Building", "Villa"] },
        render: { name: "Architectural Render", desc: "Convert 2D drawings into photorealistic renders with lighting and materials.", features: ["Drawing → Render", "Materials", "Lighting", "360°"] },
        style: { name: "Style Transfer", desc: "Transform photos between styles: Classic → Modern, East → West.", features: ["Classic → Modern", "East → West", "Minimal", "Upgrade"] },
    },

    // ====== Spa Tools ======
    spaTools: {
        skin: { name: "Skin Analysis AI", desc: "Upload a face photo — AI analyzes: acne, spots, pores, moisture.", features: ["Acne analysis", "Moisture", "Dark spots", "Pores"] },
        consult: { name: "Beauty Consultation AI", desc: "AI analyzes facial features and suggests non-surgical treatments.", features: ["Filler tips", "Contour", "Before/After", "Personalized"] },
        makeup: { name: "Virtual Makeup AI", desc: "Try makeup looks: bridal, party, natural, Korean — preview instantly.", features: ["Bridal", "Korean", "Party", "Natural"] },
        hair: { name: "Hair Design AI", desc: "Try hairstyles, hair color on real photos. Find your face-matching style.", features: ["Hairstyle", "Hair color", "Face shape", "Trending"] },
    },

    // ====== Photo Edit Tools ======
    photoTools: {
        bg: { name: "Background & Object Removal", desc: "Automatically remove background, people or objects — just 1 click.", features: ["Remove BG", "Remove object", "Remove person", "Change BG"] },
        upscale: { name: "Image Upscaling", desc: "Increase resolution 2x-4x, restore details, reduce noise with AI.", features: ["2x", "4x", "Detail", "Denoise"] },
        color: { name: "Color Correction AI", desc: "Auto color balance, enhance lighting, professional color filters.", features: ["Auto color", "HDR", "Film filter", "Light/Dark"] },
        art: { name: "Artistic Generation", desc: "Transform photos into art: oil painting, watercolor, anime, sketch.", features: ["Oil paint", "Watercolor", "Anime", "Sketch"] },
    },

    // ====== Dashboard features ======
    dashFeatures: {
        architecture: [
            { label: "Interior" }, { label: "Exterior" },
            { label: "Render" }, { label: "Style" },
        ],
        spa: [
            { label: "Skin analysis" }, { label: "Consult" },
            { label: "Makeup" }, { label: "Hair" },
        ],
        photoEdit: [
            { label: "Remove BG" }, { label: "Upscale" },
            { label: "Color" }, { label: "Art" },
        ],
    },
};

export default en;
